import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { adminApi } from "@/lib/admin-api";

export default function OwnerSetup() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ setup_secret: "", email: "", password: "", name: "Platform Owner" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => { adminApi("/admin/setup/status").then(setStatus).catch(() => setStatus({ setup_enabled: false, secret_configured: false })); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      await adminApi("/admin/setup", { method: "POST", body: JSON.stringify(form) });
      setDone(true);
    } catch (e2) { setError(e2.message || "Setup failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-midnight px-6">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-midnight-2 p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/10"><ShieldCheck className="h-5 w-5 text-brand" /></div>
          <div><p className="font-display text-lg font-bold tracking-widest text-white">OWNER SETUP</p><p className="text-xs text-white/50">Create your Platform Owner account</p></div>
        </div>

        {done ? (
          <div className="text-center" data-testid="setup-done">
            <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-success" />
            <p className="text-base font-bold text-white">Platform Owner created</p>
            <p className="mt-1 text-sm text-white/60">Setup is now permanently disabled. Sign in to your dashboard.</p>
            <button onClick={() => navigate("/owner/login")} data-testid="setup-goto-login" className="mt-5 w-full rounded-lg bg-brand py-3 text-sm font-extrabold tracking-widest text-white">GO TO LOGIN</button>
          </div>
        ) : status && !status.setup_enabled ? (
          <div data-testid="setup-disabled" className="rounded-lg bg-white/5 p-4 text-sm text-white/70">
            {status.secret_configured
              ? "Owner setup has already been completed and is disabled. If you need another owner account, contact platform support."
              : "Owner setup is not configured yet. Add a secret named OWNER_SETUP_SECRET in Emergent Secrets → Backend, then reload this page."}
            <button onClick={() => navigate("/owner/login")} className="mt-4 w-full rounded-lg border border-white/15 py-3 text-sm font-bold text-white/80">Go to login</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Setup secret" type="password" value={form.setup_secret} onChange={(v) => setForm({ ...form, setup_secret: v })} testid="setup-secret" placeholder="From Emergent Secrets" />
            <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="setup-name" />
            <Field label="Owner email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="setup-email" />
            <Field label="Password (min 10 chars)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="setup-password" />
            {error && <p data-testid="setup-error" className="rounded-md bg-danger/20 px-3 py-2 text-sm text-red-300">{error}</p>}
            <button type="submit" disabled={loading} data-testid="setup-submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-widest text-white disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "CREATE OWNER ACCOUNT"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold text-white/70">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} type={type} required placeholder={placeholder} data-testid={testid}
        className="w-full rounded-lg border border-white/15 bg-white/5 px-3.5 py-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-brand" />
    </div>
  );
}
